/**
 * Learn Mode screen. For a chosen topic it shows an authored lesson (concept,
 * method, pitfalls, vocabulary) followed by ONE live, engine-verified worked
 * example pulled from the topic's generator, and a "Practice this" button that
 * jumps straight into Practice for the same topic.
 *
 * The lesson text is authored; the worked example is generated with a fixed
 * per-topic seed so it is stable across renders and always mathematically
 * correct (it comes from the same validated generator Practice uses).
 */

import type { Screen } from "../router";
import type { Store } from "../state";
import { el, clear } from "../../ui/dom";
import { mathElement, mathToHtml } from "../../ui/math-display";
import { plotElement } from "../../graph/svg-render";
import {
  TOPICS,
  getTopic,
  isTopicId,
  type TopicId,
} from "../../curriculum/curriculum";
import {
  getLesson,
  lessonQuickChecks,
  type QuickCheck,
} from "../../curriculum/lessons";
import { subskillLabel } from "../../curriculum/subskills";
import { getGenerator, hasGenerator } from "../../topics/registry";
import type { Question } from "../../topics/types";

export const learnScreen: Screen = (store, mount) => {
  const state = store.get();
  const paramTopic = state.screenParams["topic"];
  const topic: TopicId =
    typeof paramTopic === "string" && isTopicId(paramTopic)
      ? paramTopic
      : state.settings.currentTopic;

  render(store, mount, topic);
};

function render(store: Store, mount: HTMLElement, topic: TopicId): void {
  clear(mount);
  mount.appendChild(el("h1", {}, "Learn"));

  // Topic picker (only topics that have a generator, matching Practice).
  const select = el(
    "select",
    {
      class: "select",
      "aria-label": "Lesson topic",
      onChange: (e) => {
        const value = (e.currentTarget as HTMLSelectElement).value;
        if (isTopicId(value)) render(store, mount, value);
      },
    },
    ...TOPICS.filter((t) => hasGenerator(t.id)).map((t) =>
      el("option", { value: t.id, selected: t.id === topic }, t.name),
    ),
  );
  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "Topic"),
      select,
    ),
  );

  const lesson = getLesson(topic);
  const topicMeta = getTopic(topic);

  // Concept, with an optional "key idea" recognition callout.
  const conceptCard = el(
    "section",
    { class: "card lesson-card" },
    el("p", { class: "card-title" }, topicMeta.name),
    richText("p", "lesson-intro", lesson.intro),
  );
  if (lesson.keyIdea) {
    conceptCard.appendChild(
      el(
        "div",
        { class: "key-idea" },
        el("span", { class: "key-idea-label" }, "Key idea"),
        richText("p", "key-idea-text", lesson.keyIdea),
      ),
    );
  }
  mount.appendChild(conceptCard);

  // Method.
  const steps = el("ol", { class: "lesson-steps" });
  for (const s of lesson.methodSteps) {
    steps.appendChild(richText("li", "", s));
  }
  mount.appendChild(
    el(
      "section",
      { class: "card" },
      el("p", { class: "card-title" }, "How to solve it"),
      steps,
    ),
  );

  // Worked example (live from the generator, engine-verified).
  const example = generateExample(topic);
  if (example) {
    const box = el("section", { class: "card" });
    box.appendChild(el("p", { class: "card-title" }, "Worked example"));
    const prompt = el("div", { class: "question-prompt" });
    prompt.appendChild(mathElement(example.prompt.latex ?? example.prompt.text));
    if (example.graph) prompt.appendChild(plotElement(example.graph));
    box.appendChild(prompt);

    const work = el("div", { class: "reveal" });
    work.appendChild(el("strong", {}, "Step by step"));
    for (const step of example.steps) {
      const s = el("div", { class: "reveal-step" }, el("div", {}, step.description));
      s.appendChild(mathElement(step.latex));
      work.appendChild(s);
    }
    box.appendChild(work);
    mount.appendChild(box);
  }

  // Pitfalls.
  if (lesson.pitfalls.length > 0) {
    const list = el("ul", { class: "lesson-pitfalls" });
    for (const p of lesson.pitfalls) list.appendChild(richText("li", "", p));
    mount.appendChild(
      el(
        "section",
        { class: "card" },
        el("p", { class: "card-title" }, "Watch out for"),
        list,
      ),
    );
  }

  // Vocabulary.
  if (lesson.vocabulary && lesson.vocabulary.length > 0) {
    const dl = el("dl", { class: "lesson-vocab" });
    for (const v of lesson.vocabulary) {
      dl.appendChild(richText("dt", "", v.term));
      dl.appendChild(richText("dd", "", v.definition));
    }
    mount.appendChild(
      el(
        "section",
        { class: "card" },
        el("p", { class: "card-title" }, "Key terms"),
        dl,
      ),
    );
  }

  // Subskills exercised (ties Learn to the diagnostics vocabulary).
  if (topicMeta.subskills.length > 0) {
    const chips = topicMeta.subskills.map((sk) =>
      el("span", { class: "badge" }, subskillLabel(sk)),
    );
    mount.appendChild(
      el(
        "section",
        { class: "card" },
        el("p", { class: "card-title" }, "Skills you'll use"),
        el("div", { class: "options" }, ...chips),
      ),
    );
  }

  // Check your understanding (conceptual questions, no scoring side effects).
  const checks = lessonQuickChecks(lesson);
  checks.forEach((qc, i) => {
    mount.appendChild(renderQuickCheck(qc, checks.length > 1 ? i + 1 : undefined));
  });

  // Practice this topic.
  mount.appendChild(
    el(
      "div",
      { class: "btn-row" },
      el(
        "button",
        {
          class: "btn btn-primary",
          onClick: () => store.navigate("practice", { topic }),
        },
        "Practice this",
      ),
      el(
        "button",
        { class: "btn", onClick: () => store.navigate("home") },
        "Back to Home",
      ),
    ),
  );
}

/**
 * Generate one stable, verified worked example for a topic. A fixed per-topic
 * seed keeps the example the same across renders. Generators self-validate, so
 * this is always correct; if a generator ever throws we simply omit the
 * example rather than break the lesson.
 */
function generateExample(topic: TopicId): Question | null {
  const gen = getGenerator(topic);
  if (!gen) return null;
  try {
    return gen.generate({ seed: `lesson-${topic}`, difficulty: "easy" });
  } catch {
    return null;
  }
}

/**
 * Render the conceptual "Check your understanding" card. Purely local: picking
 * a choice reveals whether it was right and shows the explanation. It never
 * touches the store, progress, or mastery - Learn teaches, it does not assess.
 */
function renderQuickCheck(qc: QuickCheck, index?: number): HTMLElement {
  const card = el("section", { class: "card quick-check" });
  const title =
    index === undefined
      ? "Check your understanding"
      : `Check your understanding ${index}`;
  card.appendChild(el("p", { class: "card-title" }, title));
  card.appendChild(richText("p", "quick-check-prompt", qc.prompt));

  const options = el("div", { class: "quick-check-options" });
  const feedback = el("div", { class: "quick-check-feedback", "aria-live": "polite" });
  let answered = false;

  const buttons: HTMLButtonElement[] = qc.choices.map((choice, i) => {
    const btn = el(
      "button",
      {
        class: "quick-check-choice",
        onClick: () => {
          if (answered) return;
          answered = true;
          const correct = i === qc.correctIndex;
          buttons.forEach((b, j) => {
            b.setAttribute("disabled", "");
            if (j === qc.correctIndex) b.classList.add("qc-correct");
            else if (j === i) b.classList.add("qc-incorrect");
          });
          clear(feedback);
          feedback.appendChild(
            el(
              "div",
              {
                class: `feedback ${correct ? "feedback-correct" : "feedback-incorrect"}`,
              },
              el(
                "span",
                { class: "feedback-icon", "aria-hidden": "true" },
                correct ? "\u2713" : "\u2717",
              ),
              el("span", {}, correct ? "Correct" : "Not quite"),
            ),
          );
          feedback.appendChild(richText("p", "quick-check-explanation", qc.explanation));
        },
      },
      richText("span", "", choice),
    ) as HTMLButtonElement;
    return btn;
  });

  for (const b of buttons) options.appendChild(b);
  card.appendChild(options);
  card.appendChild(feedback);
  return card;
}

/**
 * Build an element whose text may contain inline math delimited by $...$.
 * Non-math segments are inserted as text; math segments are rendered with
 * KaTeX. Avoids innerHTML for the plain text, using KaTeX HTML only for the
 * math spans (KaTeX output is trusted, our own strings).
 */
function richText(
  tag: "p" | "li" | "dd" | "dt" | "span",
  className: string,
  text: string,
): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  const parts = text.split(/\$([^$]+)\$/g);
  parts.forEach((part, i) => {
    if (i % 2 === 1) {
      const span = document.createElement("span");
      span.innerHTML = mathToHtml(part, false);
      node.appendChild(span);
    } else if (part) {
      node.appendChild(document.createTextNode(part));
    }
  });
  return node;
}
